import { Suspense, lazy } from "react";
import type { AnswersByKey, FunnelStep } from "../funnel/types";
import { getPersonaDataWithOverride, resolvePersonaOverrideFromUrl } from "../funnel/flowData";
import { getImageRenderMeta } from "./imageMeta";
import { OptionGlyph } from "./ui/OptionGlyph";
import { WelcomeTrustPrivacyIcon } from "./ui/WelcomeTrustIcons";
import type { PaywallPersonalization } from "../funnel/paywallPersonalization";
import { FunnelLoadingState } from "./ui/FunnelLoadingState";

type StepRendererProps = {
  step: FunnelStep;
  answers: AnswersByKey;
  onSelectSingle: (answerKey: string, optionId: string) => void;
  onToggleMulti: (answerKey: string, optionId: string) => void;
  onSetText: (answerKey: string, value: string) => void;
  onWelcomeFirstImageReadyChange?: (isReady: boolean) => void;
  onWelcomeEmbeddedSelect?: (answerKey: string, optionId: string) => void;
  onLoadingContinue: () => void;
  onPaymentContinue: () => void;
  canContinue: boolean;
  isPaymentSubmitting: boolean;
  paymentCheckoutUrl?: string | null;
  paymentErrorMessage?: string | null;
  paymentErrorCode?: string | null;
  paymentEmail?: string;
  onPaymentEmailChange?: (value: string) => void;
  paywallPersonalization?: PaywallPersonalization | null;
  restoredPlanId?: string | null;
  onPlanSelected?: (planId: string) => void;
};

const loadQuestionStep = () => import("./steps/QuestionStep");
const loadAffirmationStep = () => import("./steps/AffirmationStep");
const loadCheckoutCompleteStep = () => import("./steps/CheckoutCompleteStep");
const loadLoadingStep = () => import("./steps/LoadingStep");
const loadResultStep = () => import("./steps/ResultStep");
const loadEmailStep = () => import("./steps/EmailStep");
const loadPaymentStep = () => import("./steps/PaymentStep");

const QuestionStepLazy = lazy(async () => ({ default: (await loadQuestionStep()).QuestionStep }));
const AffirmationStepLazy = lazy(async () => ({
  default: (await loadAffirmationStep()).AffirmationStep
}));
const CheckoutCompleteStepLazy = lazy(async () => ({
  default: (await loadCheckoutCompleteStep()).CheckoutCompleteStep
}));
const LoadingStepLazy = lazy(async () => ({ default: (await loadLoadingStep()).LoadingStep }));
const ResultStepLazy = lazy(async () => ({ default: (await loadResultStep()).ResultStep }));
const EmailStepLazy = lazy(async () => ({ default: (await loadEmailStep()).EmailStep }));
const PaymentStepLazy = lazy(async () => ({ default: (await loadPaymentStep()).PaymentStep }));

const StepChunkFallback = ({ label }: { label: string }) => (
  <FunnelLoadingState label={label} showMark={false} />
);

export const preloadStepRendererChunk = (kind: FunnelStep["kind"] | undefined): Promise<unknown> => {
  switch (kind) {
    case "question":
      return loadQuestionStep();
    case "affirmation":
      return Promise.all([loadAffirmationStep(), loadCheckoutCompleteStep()]);
    case "loading":
      return loadLoadingStep();
    case "result":
      return loadResultStep();
    case "email":
      return loadEmailStep();
    case "payment":
      return loadPaymentStep();
    case "welcome":
    case undefined:
      return Promise.resolve();
  }
};

const getAnswerKeyForStep = (step: FunnelStep): string => {
  if (step.kind === "question" && step.answerKey) {
    return step.answerKey;
  }
  if (step.kind === "email") {
    return step.answerKey;
  }
  if (step.kind === "payment") {
    return step.answerKey;
  }
  return step.id;
};

export const StepRenderer = ({
  step,
  answers,
  onSelectSingle,
  onToggleMulti,
  onSetText,
  onWelcomeFirstImageReadyChange,
  onWelcomeEmbeddedSelect,
  onLoadingContinue,
  onPaymentContinue,
  canContinue,
  isPaymentSubmitting,
  paymentCheckoutUrl,
  paymentErrorMessage,
  paymentErrorCode,
  paymentEmail = "",
  onPaymentEmailChange,
  paywallPersonalization,
  restoredPlanId,
  onPlanSelected
}: StepRendererProps) => {
  if (step.kind === "welcome") {
    const heroImageMeta = getImageRenderMeta(step.heroImage);

    return (
      <section className="step step-welcome">
        <div className="welcome-intro">
          <h1 className="welcome-headline" dangerouslySetInnerHTML={{ __html: step.title }} />
          {step.translation ? (
            <p className="welcome-translation">{step.translation}</p>
          ) : null}
          {step.description.some(Boolean) ? (
            <div className="welcome-subheadlines">
              {step.description.filter(Boolean).map((line, i) => (
                <p key={i} className="welcome-subheadline">
                  {line}
                </p>
              ))}
            </div>
          ) : null}
          <div className="welcome-trust-block" aria-label="Community trust">
            <div className="welcome-trust-social">
              {step.trustBadgeAvatars?.length ? (
                <div className="welcome-trust-avatars" aria-hidden="true">
                  {step.trustBadgeAvatars.map((avatar, index) => (
                    <img
                      key={`${avatar.src}-${index}`}
                      src={avatar.src}
                      alt=""
                      className="welcome-trust-avatar"
                      width={32}
                      height={32}
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </div>
              ) : null}
              <p className="welcome-trust-member-copy">
                <span
                  className="welcome-trust-count"
                  dangerouslySetInnerHTML={{ __html: step.ratingText }}
                />
                {step.trustMemberSubtitle ? (
                  <span className="welcome-trust-member-sub">
                    {step.trustMemberSubtitle}
                  </span>
                ) : null}
              </p>
            </div>
            {step.trustPrivacyItems?.length ? (
              <div className="welcome-trust-privacy-bar">
                {step.trustPrivacyItems.map((item) => (
                  <span key={item.label} className="welcome-trust-privacy-item">
                    <WelcomeTrustPrivacyIcon
                      icon={item.icon}
                      className="welcome-trust-privacy-icon"
                    />
                    <span>{item.label}</span>
                  </span>
                ))}
              </div>
            ) : step.trustBadgeCaption ? (
              <p className="welcome-trust-desc">{step.trustBadgeCaption}</p>
            ) : null}
          </div>
        </div>
        {step.embeddedQuestion ? (
          <div className="welcome-embedded-question">
            <h2 className="welcome-embedded-question-title">{step.embeddedQuestion.title}</h2>
            <div className="welcome-embedded-options">
              {step.embeddedQuestion.options.map((option) => (
                (() => {
                  const optionImageMeta = getImageRenderMeta(option.image);

                  return (
                    <button
                      key={option.id}
                      type="button"
                      className="option-card welcome-embedded-option-card"
                      onClick={() =>
                        (onWelcomeEmbeddedSelect ?? onSelectSingle)(
                          step.embeddedQuestion!.answerKey,
                          option.id
                        )
                      }
                    >
                      <div className="welcome-embedded-option-media">
                        {option.image ? (
                          <img
                            src={option.image}
                            width={optionImageMeta?.width}
                            height={optionImageMeta?.height}
                            alt=""
                            className="welcome-embedded-option-image"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <span className="welcome-embedded-option-icon-shell" aria-hidden="true">
                            <OptionGlyph optionId={option.id} />
                          </span>
                        )}
                      </div>
                      <div className="welcome-embedded-option-copy">
                        <span className="option-label">{option.label}</span>
                        {option.description ? (
                          <span className="welcome-embedded-option-description">
                            {option.description}
                          </span>
                        ) : null}
                      </div>
                      <span className="welcome-embedded-option-chevron" aria-hidden>
                        ›
                      </span>
                    </button>
                  );
                })()
              ))}
            </div>
            <p className="welcome-embedded-trust">No commitment · about 60 seconds</p>
          </div>
        ) : step.heroVideo ? (
          <div className="welcome-hero-static">
            <video
              className="welcome-hero-image welcome-hero-video"
              src={step.heroVideo}
              poster={step.heroImage}
              autoPlay
              muted
              loop
              playsInline
              onLoadedData={() => onWelcomeFirstImageReadyChange?.(true)}
              onError={() => onWelcomeFirstImageReadyChange?.(true)}
            />
          </div>
        ) : step.heroImage ? (
          <div className="welcome-hero-static">
            <img
              src={step.heroImage}
              alt=""
              className="welcome-hero-image welcome-hero-image-rounded"
              width={heroImageMeta?.width ?? 600}
              height={heroImageMeta?.height ?? 751}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              onLoad={() => onWelcomeFirstImageReadyChange?.(true)}
              onError={(event) => {
                const img = event.currentTarget;
                const fallbackSrc = "/sehaj/Imagery/hand-reading-sikh-story.webp";
                if (!img.dataset.fallbackApplied && img.src !== fallbackSrc) {
                  img.dataset.fallbackApplied = "true";
                  img.src = fallbackSrc;
                  return;
                }
                onWelcomeFirstImageReadyChange?.(true);
              }}
            />
          </div>
        ) : null}
      </section>
    );
  }

  if (step.kind === "question") {
    const answerKey = getAnswerKeyForStep(step);
    const value = answers[answerKey];
    return (
      <Suspense fallback={null}>
        <QuestionStepLazy
          step={step}
          value={typeof value === "string" || Array.isArray(value) ? value : undefined}
          onSelectSingle={(optionId) => onSelectSingle(answerKey, optionId)}
          onToggleMulti={(optionId) => onToggleMulti(answerKey, optionId)}
        />
      </Suspense>
    );
  }

  if (step.kind === "affirmation") {
    return (
      <Suspense fallback={null}>
        {step.id === "checkout_complete" ? (
          <CheckoutCompleteStepLazy
            step={step}
            checkoutEmail={typeof answers.email === "string" ? answers.email : undefined}
          />
        ) : (
          <AffirmationStepLazy step={step} />
        )}
      </Suspense>
    );
  }

  if (step.kind === "loading") {
    const modalValue = answers[step.modalQuestion.answerKey];
    return (
      <Suspense fallback={<StepChunkFallback label="Preparing your Sehaj profile…" />}>
        <LoadingStepLazy
          step={step}
          modalValue={typeof modalValue === "string" ? modalValue : undefined}
          onSelectModal={(optionId) => onSelectSingle(step.modalQuestion.answerKey, optionId)}
          onContinue={onLoadingContinue}
        />
      </Suspense>
    );
  }

  if (step.kind === "result") {
    const personaOverride = resolvePersonaOverrideFromUrl();
    const personaData = getPersonaDataWithOverride(answers, personaOverride);
    return (
      <Suspense fallback={<StepChunkFallback label="Loading your Sehaj path…" />}>
        <ResultStepLazy step={{ ...step, ...personaData }} />
      </Suspense>
    );
  }

  if (step.kind === "email") {
    const answerKey = getAnswerKeyForStep(step);
    const email = answers[answerKey];
    return (
      <Suspense fallback={null}>
        <EmailStepLazy
          step={step}
          email={typeof email === "string" ? email : ""}
          onEmailChange={(value) => onSetText(answerKey, value)}
          onSubmit={onPaymentContinue}
          canSubmit={canContinue}
        />
      </Suspense>
    );
  }

  const answerKey = getAnswerKeyForStep(step);
  const selectedPlan = answers[answerKey];

  return (
    <Suspense fallback={null}>
      <PaymentStepLazy
        step={step}
        selectedPlan={typeof selectedPlan === "string" ? selectedPlan : ""}
        onSelectPlan={(planId) => {
          onSetText(answerKey, planId);
          onPlanSelected?.(planId);
        }}
        onProceedPayment={onPaymentContinue}
        isSubmitting={isPaymentSubmitting}
        errorMessage={paymentErrorMessage}
        errorCode={paymentErrorCode}
        checkoutUrl={paymentCheckoutUrl}
        checkoutEmail={paymentEmail}
        onCheckoutEmailChange={(value) => onPaymentEmailChange?.(value)}
        personalization={paywallPersonalization}
        restoredPlanId={restoredPlanId}
      />
    </Suspense>
  );
};
